// The vendor register (spec §4, §25; ADR 0016). A vendor is an
// organization BRE buys from, not a separate kind of record: the vendor
// profile hangs off the organization, and the organization keeps its own
// name, contacts, notes and merge history.
//
// Hub has no QuickBooks connection yet. The QuickBooks vendor id and name
// here are typed by staff and never checked against QuickBooks (rule 3:
// QuickBooks stays the system of record for bills and payments).
//
// Compliance (W-9, certificate of insurance, agreement) runs on the
// requirements and reminders of ADR 0007: a "Vendor compliance" checklist
// is stamped on the vendor, and filing a document satisfies its item.

import { z } from 'zod'

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()

export const VENDOR_STATUSES = ['prospective', 'active', 'on_hold', 'inactive'] as const
export type VendorStatus = (typeof VENDOR_STATUSES)[number]
export const VENDOR_STATUS_LABELS: Record<VendorStatus, string> = { prospective: 'Prospective', active: 'Active', on_hold: 'On hold', inactive: 'Inactive' }

export const W9_STATUSES = ['not_requested', 'requested', 'received', 'expired'] as const
export type W9Status = (typeof W9_STATUSES)[number]
export const W9_STATUS_LABELS: Record<W9Status, string> = { not_requested: 'Not requested', requested: 'Requested', received: 'Received', expired: 'Expired' }

export const VENDOR_CONTACT_ROLES = ['primary', 'billing', 'ordering', 'on_site', 'sales', 'other'] as const
export type VendorContactRole = (typeof VENDOR_CONTACT_ROLES)[number]
export const VENDOR_CONTACT_ROLE_LABELS: Record<VendorContactRole, string> = {
  primary: 'Primary contact',
  billing: 'Billing',
  ordering: 'Ordering',
  on_site: 'On site',
  sales: 'Sales',
  other: 'Other',
}

export const VENDOR_DOCUMENT_KINDS = ['w9', 'coi', 'agreement'] as const
export type VendorDocumentKind = (typeof VENDOR_DOCUMENT_KINDS)[number]
export const VENDOR_DOCUMENT_KIND_LABELS: Record<VendorDocumentKind, string> = { w9: 'W-9', coi: 'Certificate of insurance', agreement: 'Agreement' }

// The badge on the vendor list, worst first.
//   expired   a certificate, agreement or W-9 ran out and nothing replaced it
//   missing   something on the compliance checklist is still open, or there
//             is no checklist yet
//   expiring  everything is in, but something runs out within 30 days
//   complete  everything is satisfied, waived or not applicable
export const VENDOR_COMPLIANCE_STATES = ['expired', 'missing', 'expiring', 'complete'] as const
export type VendorComplianceState = (typeof VENDOR_COMPLIANCE_STATES)[number]
export const VENDOR_COMPLIANCE_LABELS: Record<VendorComplianceState, string> = { expired: 'Expired', missing: 'Missing', expiring: 'Expiring', complete: 'Complete' }
export const VENDOR_EXPIRING_DAYS = 30

// The checklist the migration seeds and that is stamped on a vendor when it
// becomes active (or is made from a vendor application).
export const VENDOR_COMPLIANCE_CHECKLIST_ID = 'ckt_vendor_compliance'

export const VENDOR_ENGAGEMENT_TYPES = ['event', 'project'] as const
export type VendorEngagementType = (typeof VENDOR_ENGAGEMENT_TYPES)[number]

export const QBO_NOT_CONNECTED_NOTE = 'Hub has no QuickBooks connection yet. The QuickBooks vendor is typed here by hand and never checked against QuickBooks.'

// ------------------------------------------------------------------ shapes

export type VendorCategory = { id: string; name: string; sortOrder: number; retiredAt: string | null; vendorCount: number }

export type VendorComplianceItem = {
  requirementId: string
  title: string
  status: 'open' | 'satisfied' | 'waived' | 'not_applicable' | 'expired'
  dueOn: string | null
  expiresOn: string | null
}

export type VendorCompliance = {
  state: VendorComplianceState
  // Whether the compliance checklist has been stamped on this vendor.
  stamped: boolean
  items: VendorComplianceItem[]
  // The earliest date something on file runs out, if anything does.
  nextExpiry: string | null
}

export type VendorSummary = {
  organizationId: string
  name: string
  status: VendorStatus
  categoryId: string | null
  categoryName: string | null
  w9Status: W9Status
  compliance: VendorComplianceState
  nextExpiry: string | null
  qboVendorId: string | null
  qboDisplayName: string | null
  primaryContactName: string | null
  updatedAt: string
}

export type VendorDocument = {
  id: string
  kind: VendorDocumentKind
  // The catalog document. Its title shows only to someone who can open it
  // (a W-9 is always confidential: it carries a tax id).
  documentId: string
  documentVersion: number
  visible: boolean
  title: string | null
  startsOn: string | null
  endsOn: string | null
  note: string | null
  recordedByName: string | null
  recordedAt: string
  retiredAt: string | null
  retireReason: string | null
}

export type VendorContact = {
  id: string
  personId: string
  personName: string
  role: VendorContactRole
  note: string | null
  addedAt: string
}

export type VendorEngagement = {
  id: string
  targetType: VendorEngagementType
  targetId: string
  // Null when the viewer can't see events (or projects).
  label: string | null
  note: string | null
  addedAt: string
}

export type Vendor = VendorSummary & {
  paymentTerms: string | null
  website: string | null
  accountNumber: string | null
  notes: string | null
  w9RequestedOn: string | null
  qboMappedByName: string | null
  qboMappedAt: string | null
  sourceSubmissionId: string | null
  complianceDetail: VendorCompliance
  documents: VendorDocument[]
  contacts: VendorContact[]
  engagements: VendorEngagement[]
  // Shown only to holders of the capability that sees them; hidden counts
  // say how many more there are.
  assets: { id: string; name: string; tag: string }[] | null
  workOrders: { id: string; number: number; title: string; status: string }[] | null
  createdAt: string
  createdByName: string | null
  version: number
  can: { manage: boolean }
}

// ------------------------------------------------------------------ bodies

const VendorFields = {
  categoryId: ID.nullable().optional(),
  paymentTerms: optionalText(500),
  website: optionalText(300),
  accountNumber: optionalText(100),
  notes: optionalText(4000),
}

export const VendorCreateBody = z.object({
  organizationId: ID,
  status: z.enum(VENDOR_STATUSES).optional(),
  ...VendorFields,
  // Made from an accepted vendor application (a form's organization
  // proposal, ADR 0011): starts prospective with the checklist stamped.
  fromSubmissionId: ID.optional(),
})

export const VendorPatch = z.object({
  ...VendorFields,
  status: z.enum(VENDOR_STATUSES).optional(),
  // "received" comes only from filing a W-9 document.
  w9Status: z.enum(['not_requested', 'requested', 'expired']).optional(),
  expectedVersion: z.number().int().min(1),
})

export const VendorQboBody = z.object({
  qboVendorId: text(60).min(1).nullable(),
  qboDisplayName: optionalText(200),
  expectedVersion: z.number().int().min(1),
})

export const VendorDocumentBody = z
  .object({
    kind: z.enum(VENDOR_DOCUMENT_KINDS),
    documentId: ID,
    version: z.number().int().min(1),
    startsOn: DAY.nullable().optional(),
    // A certificate's expiry, or an agreement's last day.
    endsOn: DAY.nullable().optional(),
    note: optionalText(1000),
  })
  .refine((b) => b.kind !== 'coi' || !!b.endsOn, { message: 'A certificate of insurance needs its expiry date', path: ['endsOn'] })
  .refine((b) => !b.startsOn || !b.endsOn || b.endsOn >= b.startsOn, { message: 'It must end on or after it starts', path: ['endsOn'] })

export const VendorDocumentRetireBody = z.object({ reason: text(500).min(3) })

export const VendorContactBody = z.object({ personId: ID, role: z.enum(VENDOR_CONTACT_ROLES), note: optionalText(500) })

export const VendorEngagementBody = z.object({ targetType: z.enum(VENDOR_ENGAGEMENT_TYPES), targetId: ID, note: optionalText(500) })

export const VendorCategoryBody = z.object({ name: text(100).min(1), sortOrder: z.number().int().min(0).max(1000).optional() })
